import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useAmendFinancialClosingMutation } from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import * as Yup from "yup";

type AmendClosingFormValues = {
  reason: string;
};

type AmendClosingModalProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  financialClosingId: string;
};

const amendClosingValidationSchema = Yup.object().shape({
  reason: Yup.string().trim().required("Please describe what needs to change"),
});

export const AmendClosingModal = ({
  open,
  onClose,
  entityId,
  financialClosingId,
}: AmendClosingModalProps) => {
  const { alertToast, successToast } = useToast();
  const [amendClosing, { isLoading }] = useAmendFinancialClosingMutation();

  const handleSubmit = async ({ reason }: AmendClosingFormValues) => {
    try {
      await amendClosing({
        entityId,
        financialClosingId,
        reason,
      }).unwrap();
      successToast({ message: "New version created" });
      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content onClick={(e) => e.stopPropagation()}>
        <FormikForm
          initialValues={{ reason: "" }}
          validationSchema={amendClosingValidationSchema}
          onSubmit={handleSubmit}
          noValidate
        >
          <>
            <Modal.Header>
              <Modal.Title>Amend closing</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="t-flex t-flex-col t-gap-4">
              <p className="t-m-0 t-text-body t-text-text-60">
                This action will mark the current closing as amended and will
                create a new closing, which can be later sent to review.
              </p>
              <TextArea
                name="reason"
                label="What needs to change"
                placeholder="Describe"
                required
                rows={3}
                autoResize
              />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button type="button" disabled={isLoading}>
                  Cancel
                </Button>
              </Modal.RawClose>
              <Button
                type="submit"
                customType="danger"
                isLoading={isLoading}
                disabled={isLoading}
              >
                Amend & create new version
              </Button>
            </Modal.FooterButtonGroup>
          </>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
